import { useState, useEffect, useCallback } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';

export type HomeScreenOption = 'location_calendar' | 'mobile_calendar';

// Define all pages that can be disabled
export type PageId = 
  // Top navigation
  | 'calendar'
  | 'calendarV2'
  | 'mobile-calendar'
  | 'bookings'
  | 'proposals'
  | 'payments'
  | 'customers'
  // Side navigation
  | 'messages'
  | 'crm'
  | 'marketing'
  | 'payroll'
  | 'timeclock'
  | 'supplies'
  | 'services'
  | 'resources'
  | 'automations'
  | 'tasks'
  | 'verification'
  | 'reminder-status'
  | 'console'
  | 'sales-reports'
  | 'team-reports'
  | 'supplies-usage'
  | 'dialler'
  | 'settings'
  // Settings pages (only non-mandatory ones that can be disabled)
  | 'flat-rates'
  | 'materials'
  | 'operating-costs'
  | 'application-settings'
  | 'online-bookings';

interface ApplicationSettings {
  home_screen: HomeScreenOption;
  disable_pages: PageId[];
}

export function useApplicationSettings() {
  const { selectedBusiness } = useBusiness();
  const [settings, setSettings] = useState<ApplicationSettings>({
    home_screen: 'location_calendar',
    disable_pages: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to clean up duplicate settings entries
  const cleanupDuplicateSettings = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      // Get all settings entries for this business
      const { data, error } = await supabase
        .from('application_settings')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      // If there are multiple entries, keep only the oldest one
      if (data && data.length > 1) {
        console.log(`Found ${data.length} application settings entries for business ${selectedBusiness.id}, cleaning up...`);
        
        // Keep the first entry (oldest) and delete the rest
        const [keepEntry, ...duplicateEntries] = data;
        
        // Extract the IDs of duplicate entries
        const duplicateIds = duplicateEntries.map(entry => entry.id);
        
        // Delete duplicate entries
        if (duplicateIds.length > 0) {
          const { error: deleteError } = await supabase
            .from('application_settings')
            .delete()
            .in('id', duplicateIds);
          
          if (deleteError) throw deleteError;
          
          console.log(`Successfully removed ${duplicateIds.length} duplicate application settings entries`);
        }
      }
    } catch (err) {
      console.error('Error cleaning up duplicate settings:', err);
    }
  }, [selectedBusiness?.id]);

  // Function to load settings from the database
  const loadSettings = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) {
      console.log('No business selected or supabase not available');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    console.log(`Loading application settings for business ${selectedBusiness.id}`);
    
    // First clean up any duplicate settings
    await cleanupDuplicateSettings();
    
    try {
      // Use the cache: 'no-cache' option to ensure we always get fresh data
      const { data, error } = await supabase
        .from('application_settings')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching application settings:', error);
        throw error;
      }
      
      if (data) {
        console.log('Loaded application settings:', data);
        setSettings({
          home_screen: data.home_screen || 'location_calendar',
          disable_pages: Array.isArray(data.disable_pages) ? data.disable_pages : [],
        });
      } else {
        console.log('No application settings found, creating defaults');
        // Create default settings if none exist
        await createDefaultSettings();
      }
    } catch (err) {
      console.error('Error loading application settings:', err);
      setError('Failed to load application settings');
    } finally {
      setIsLoading(false);
    }
  }, [selectedBusiness?.id, cleanupDuplicateSettings]);

  // Create default settings for a business
  const createDefaultSettings = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      const { error } = await supabase
        .from('application_settings')
        .insert({
          business_id: selectedBusiness.id,
          home_screen: 'location_calendar',
          disable_pages: [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
      
      // Set local state to default values
      setSettings({
        home_screen: 'location_calendar',
        disable_pages: [],
      });
    } catch (err) {
      console.error('Error creating default application settings:', err);
      setError('Failed to create default application settings');
    }
  }, [selectedBusiness?.id]);

  // Update settings
  const updateSettings = useCallback(async (newSettings: Partial<ApplicationSettings>) => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      // Check if settings exist
      const { data: existingSettings, error: checkError } = await supabase
        .from('application_settings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .limit(1)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingSettings) {
        // Update existing settings
        const { error } = await supabase
          .from('application_settings')
          .update({
            ...newSettings,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSettings.id);

        if (error) throw error;
      } else {
        // Create settings if they don't exist
        const { error } = await supabase
          .from('application_settings')
          .insert({
            business_id: selectedBusiness.id,
            ...newSettings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      // Update local state
      setSettings(prevSettings => ({
        ...prevSettings,
        ...newSettings
      }));
    } catch (err) {
      console.error('Error updating application settings:', err);
      setError('Failed to update application settings');
    }
  }, [selectedBusiness?.id]);

  // Load settings when the selected business changes
  useEffect(() => {
    loadSettings();
  }, [loadSettings, selectedBusiness?.id]);

  return {
    settings,
    isLoading,
    error,
    updateSettings,
    loadSettings
  };
} 